import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Stack } from '@mui/material';

const guards = new Map<string, string>();
export function confirmUnsavedChanges(path?: string) {
  return !Array.from(guards.values()).some(p => !path || p === path) || window.confirm('You have unsaved changes. Leave this form? A recovery copy will remain in this browser.');
}

export function useDraftRecovery<T>(key: string, path: string, value: T, enabled: boolean, meaningful: boolean, restore: (value: T) => void) {
  const serialized = JSON.stringify(value);
  const [baseline, setBaseline] = useState({ key, value: serialized });
  const [recovery, setRecovery] = useState<{ value: T; savedAt: number } | null>(null);
  const [storageError, setStorageError] = useState(false);
  const current = useRef(serialized);
  const wasEnabled = useRef(enabled);
  current.current = serialized;
  const dirty = enabled && wasEnabled.current && meaningful && key === baseline.key && serialized !== baseline.value;
  useEffect(() => {
    setBaseline({ key, value: current.current });
    try {
      const stored = JSON.parse(localStorage.getItem(key) || 'null');
      if (stored?.format === 1 && Date.now() - stored.savedAt < 7 * 86400000) setRecovery(stored);
      else { setRecovery(null); localStorage.removeItem(key); }
    } catch { setRecovery(null); }
  }, [key]);
  useEffect(() => {
    if (enabled && !wasEnabled.current) setBaseline({ key, value: current.current });
    wasEnabled.current = enabled;
  }, [enabled, key]);
  useEffect(() => {
    if (dirty) guards.set(key, path); else guards.delete(key);
    return () => { guards.delete(key); };
  }, [dirty, key, path]);
  useEffect(() => {
    if (!dirty || recovery) return;
    // Persist each committed edit so a crash during navigation cannot lose the last row.
    try { localStorage.setItem(key, JSON.stringify({ format: 1, savedAt: Date.now(), value: JSON.parse(serialized) })); setStorageError(false); }
    catch { setStorageError(true); }
  }, [dirty, key, recovery, serialized]);
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => { if (dirty) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);
  const markSaved = () => { guards.delete(key); setBaseline({ key, value: current.current }); setRecovery(null); try { localStorage.removeItem(key); } catch {} };
  const panel = recovery && enabled ? <Alert severity="warning" sx={{ my: 1 }} action={<Stack direction="row"><Button onClick={() => { restore(recovery.value); setRecovery(null); }}>Restore</Button><Button onClick={() => { localStorage.removeItem(key); setRecovery(null); }}>Discard</Button></Stack>}>Unsaved work from {new Date(recovery.savedAt).toLocaleString()} is available. Review recovered values and reselect any local attachments.</Alert> : storageError ? <Alert severity="warning">Browser recovery is unavailable. Save a draft before leaving.</Alert> : null;
  return { dirty, markSaved, panel };
}
